import { isHotKey } from '@lumapps/utils/browser/isHotKey';
import { HandlerMatcher, onKey } from '@lumapps/utils/event/onKey';
import { createParagraph } from '@lumapps/wrex-typography/utils/createParagraph';
import { Editor, Element, Node, Transforms, Range } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';

/** Predicate matching the textual block element (e.g. `isCodeBlock`, `isInfoPanel`), as returned by `isElementType`. */
export type IsTextualBlock = (node: Node) => boolean;

// Parse the hotkey matchers once at module load rather than on every keystroke.
const isEnter = isHotKey('Enter');
const isArrowLeft = isHotKey('ArrowLeft');
const isArrowUp = isHotKey('ArrowUp');

/**
 * 'ArrowUp' / 'ArrowLeft' pressed at the start of a textual block that is the first block of the document:
 * insert a paragraph above so the user can escape the textual block upward.
 *
 * The caret must be at the edge the arrow would leave from, otherwise the arrow must keep its native behaviour
 * (move one character/line within the block):
 * - 'ArrowLeft' only escapes from the very start of the block,
 * - 'ArrowUp' only escapes from its first line.
 */
const isArrowPressedOnFirstTextualBlock = (editor: Editor, isBlock: IsTextualBlock) => (event: KeyboardEvent) => {
    const pressedLeft = isArrowLeft(event);
    const pressedUp = isArrowUp(event);
    if (!pressedLeft && !pressedUp) {
        return false;
    }

    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return false;
    }

    const blockEntry = findParent(editor, selection.focus.path, (parent) => isBlock(parent));
    if (!blockEntry) {
        return false;
    }

    // Only escape upward when there is no block above to move into.
    if (Editor.before(editor, [selection.focus.path[0]])) {
        return false;
    }

    const [, blockPath] = blockEntry;
    if (pressedLeft) {
        // ArrowLeft anywhere but the block start should just move the caret one character to the left.
        return Editor.isStart(editor, selection.focus, blockPath);
    }

    // ArrowUp from any line but the first should just move the caret up within the block.
    const textBeforeCaret = Editor.string(editor, { anchor: Editor.start(editor, blockPath), focus: selection.focus });
    return !textBeforeCaret.includes('\n');
};

/**
 * Key bindings shared by every textual block:
 * - 'Enter' inside the block inserts a line break instead of splitting the block into a new one.
 * - 'ArrowUp' / 'ArrowLeft' on a leading textual block inserts a paragraph above it, so the user can
 *   escape a block that is the first block of the document.
 */
export const getTextualBlockKeyBindings = (editor: Editor, isBlock: IsTextualBlock): HandlerMatcher[] => [
    {
        match: (event: KeyboardEvent) => {
            if (!isEnter(event)) {
                return false;
            }
            const { selection } = editor;
            if (!selection) {
                return false;
            }
            const parentEntry = findParent(
                editor,
                selection.focus.path,
                (node) => Element.isElement(node) && Editor.isBlock(editor, node),
            );
            return !!parentEntry && isBlock(parentEntry[0]);
        },
        handler: () => {
            editor.insertText('\n');
        },
    },
    {
        match: isArrowPressedOnFirstTextualBlock(editor, isBlock),
        handler: () => {
            Transforms.insertNodes(editor, createParagraph(), { at: [0], select: true });
        },
    },
];

/**
 * Ready-to-use `getKeyHandler` for a textual block plugin with no extra key bindings.
 *
 * @example createPlugin({ getKeyHandler: createTextualBlockKeyHandler(isInfoPanel), ... })
 */
export const createTextualBlockKeyHandler = (isBlock: IsTextualBlock) => (editor: Editor) =>
    onKey(getTextualBlockKeyBindings(editor, isBlock), { preventDefault: true });
